import { create } from 'zustand';
import type {
  EditorRunState,
  NodeRunData,
  NodePinData,
  PinDataSource,
  ExecutionWorkflowMismatch,
  DirtyNodesResult,
} from '@/types/editorRunData';
import type { WorkflowDefinition, Action, Trigger } from '@/types/workflowDefinition';
import type { AgentIterationInfo } from '@/services/monitoringApi';

interface EditorRunStoreState {
  /** Current loaded run state (null if no execution loaded) */
  runState: EditorRunState | null;

  /** Whether we're in execution/debug mode */
  isExecutionMode: boolean;

  /**
   * Monotonic counter incremented on every run-state update
   * (loadExecution and mergeStreamingExecution).
   * DesignerLayout subscribes to this so useMemo retriggers on streaming updates
   * (where isExecutionMode stays true but nodeRunData changes).
   */
  runStateVersion: number;

  /** For agent nodes: currently selected iteration index (legacy, kept for compatibility) */
  selectedIterationIndex: number | null;

  /** Agent iterations state - tracks current iteration per agent name */
  agentIterations: Record<string, number>;

  /** Whether the session warning has been shown */
  hasShownSessionWarning: boolean;
}

interface EditorRunStoreActions {
  // Load/clear execution
  /**
   * Load execution data into the editor.
   * This transforms the run state and enters execution mode.
   */
  loadExecution: (runState: EditorRunState) => void;

  /**
   * Merge a streaming execution update into the current state.
   * Unlike loadExecution, this preserves pinData and agentIterations
   * so live streaming updates don't wipe user-pinned data or selections.
   */
  mergeStreamingExecution: (runState: EditorRunState) => void;

  /**
   * Clear execution data and exit execution mode.
   */
  clearExecution: () => void;

  // Pin data management
  /**
   * Pin data for a node. This freezes the output data so it can be
   * used for testing without re-executing the node.
   */
  pinNodeData: (nodeId: string, data: unknown, source: PinDataSource) => void;

  /**
   * Remove pinned data for a node.
   */
  unpinNodeData: (nodeId: string) => void;

  /**
   * Clear all pinned data.
   */
  clearAllPins: () => void;

  // Getters
  /**
   * Get run data for a specific node.
   */
  getNodeRunData: (nodeId: string) => NodeRunData | undefined;

  /**
   * Get pin data for a specific node.
   */
  getNodePinData: (nodeId: string) => NodePinData | undefined;

  /**
   * Check if a node has run data.
   */
  hasRunData: (nodeId: string) => boolean;

  /**
   * Check if a node is pinned.
   */
  isPinned: (nodeId: string) => boolean;

  /**
   * Get the effective output for a node (pinned data takes precedence).
   */
  getEffectiveOutput: (nodeId: string) => unknown | undefined;

  // Agent iteration support
  /**
   * Set the selected iteration index for viewing agent execution details.
   */
  setSelectedIteration: (index: number | null) => void;

  /**
   * Set the current iteration for a specific agent.
   */
  setAgentIteration: (agentName: string, iteration: number) => void;

  /**
   * Get the current iteration for a specific agent.
   */
  getAgentIteration: (agentName: string) => number;

  /**
   * Get agent iterations data as a Map for the WorkflowDesigner.
   * Returns Map<agentName, AgentIterationInfo[]> matching the monitoring API format
   */
  getAgentIterationsData: () => Map<string, AgentIterationInfo[]>;

  // Workflow comparison
  /**
   * Compare loaded execution with a workflow to detect mismatches.
   */
  detectWorkflowMismatch: (workflow: WorkflowDefinition) => ExecutionWorkflowMismatch;

  /**
   * Detect dirty nodes by comparing original workflow (from execution) with current workflow.
   * A node is dirty if its parameters or connections have changed since the execution.
   */
  detectDirtyNodes: (currentWorkflow: WorkflowDefinition) => DirtyNodesResult;

  /**
   * Check if a specific node is dirty.
   */
  isNodeDirty: (nodeId: string, currentWorkflow: WorkflowDefinition) => boolean;

  // Session warning
  /**
   * Mark that the session warning has been shown.
   */
  markSessionWarningShown: () => void;

  /**
   * Get node executions as NodeExecution[] for the WorkflowDesigner.
   * This converts the internal NodeRunData format to monitoring format.
   */
  getNodeExecutions: () => import('@/types/monitoring').NodeExecution[];
}

type EditorRunStore = EditorRunStoreState & EditorRunStoreActions;

export const useEditorRunStore = create<EditorRunStore>()((set, get) => ({
  // Initial state
  runState: null,
  isExecutionMode: false,
  runStateVersion: 0,
  selectedIterationIndex: null,
  agentIterations: {},
  hasShownSessionWarning: false,

  // Actions
  loadExecution: (runState: EditorRunState) => {
    set((prev) => ({
      runState,
      isExecutionMode: true,
      runStateVersion: prev.runStateVersion + 1,
      selectedIterationIndex: null,
      agentIterations: {},
    }));
  },

  mergeStreamingExecution: (runState: EditorRunState) => {
    set((prev) => ({
      runState: {
        ...runState,
        // Preserve user-pinned data from the previous state
        pinData: { ...runState.pinData, ...prev.runState?.pinData },
      },
      isExecutionMode: true,
      runStateVersion: prev.runStateVersion + 1,
      // Preserve agentIterations and selectedIterationIndex during streaming
      selectedIterationIndex: prev.selectedIterationIndex,
      agentIterations: prev.agentIterations,
    }));
  },

  clearExecution: () => {
    set({
      runState: null,
      isExecutionMode: false,
      selectedIterationIndex: null,
      agentIterations: {},
    });
  },

  pinNodeData: (nodeId: string, data: unknown, source: PinDataSource) => {
    const { runState } = get();
    if (!runState) return;

    const newPinData: NodePinData = {
      nodeId,
      data,
      pinnedAt: new Date().toISOString(),
      source,
    };

    set({
      runState: {
        ...runState,
        pinData: {
          ...runState.pinData,
          [nodeId]: newPinData,
        },
      },
    });
  },

  unpinNodeData: (nodeId: string) => {
    const { runState } = get();
    if (!runState) return;

    const newPinData = { ...runState.pinData };
    delete newPinData[nodeId];

    set({
      runState: {
        ...runState,
        pinData: newPinData,
      },
    });
  },

  clearAllPins: () => {
    const { runState } = get();
    if (!runState) return;

    set({
      runState: {
        ...runState,
        pinData: {},
      },
    });
  },

  getNodeRunData: (nodeId: string) => {
    const { runState } = get();
    return runState?.nodeRunData[nodeId];
  },

  getNodePinData: (nodeId: string) => {
    const { runState } = get();
    return runState?.pinData[nodeId];
  },

  hasRunData: (nodeId: string) => {
    const { runState } = get();
    return !!runState?.nodeRunData[nodeId];
  },

  isPinned: (nodeId: string) => {
    const { runState } = get();
    return !!runState?.pinData[nodeId];
  },

  getEffectiveOutput: (nodeId: string) => {
    const { runState } = get();
    if (!runState) return undefined;

    // Pinned data takes precedence
    const pinData = runState.pinData[nodeId];
    if (pinData) {
      return pinData.data;
    }

    // Fall back to run data
    const runData = runState.nodeRunData[nodeId];
    return runData?.output;
  },

  setSelectedIteration: (index: number | null) => {
    set({ selectedIterationIndex: index });
  },

  setAgentIteration: (agentName: string, iteration: number) => {
    set((state) => ({
      agentIterations: {
        ...state.agentIterations,
        [agentName]: iteration,
      },
    }));
  },

  getAgentIteration: (agentName: string) => {
    return get().agentIterations[agentName] ?? 0;
  },

  getAgentIterationsData: () => {
    const { runState } = get();
    const result = new Map<string, AgentIterationInfo[]>();

    if (!runState) return result;

    // Find all agent nodes and extract their iterations
    for (const nodeData of Object.values(runState.nodeRunData)) {
      if (nodeData.iterations && nodeData.iterations.length > 0) {
        // Transform from editorRunData format to AgentIterationInfo format (monitoring API)
        const transformedIterations: AgentIterationInfo[] = nodeData.iterations.map((iter) => ({
          name: String(iter.index).padStart(6, '0'), // e.g., "000000"
          index: iter.index,
          status: iter.status,
          startTime: iter.startTime,
          endTime: iter.endTime,
          duration: iter.duration,
          // Transform toolExecutions to tools format
          tools: (iter.toolExecutions ?? []).map((tool) => ({
            toolName: tool.toolName,
            iterations: 1, // Tool ran once in this iteration
            status: tool.status,
          })),
        }));
        // Use nodeName as the key (matches how Run view uses agentName)
        result.set(nodeData.nodeName, transformedIterations);
      }
    }

    return result;
  },

  detectWorkflowMismatch: (workflow: WorkflowDefinition) => {
    const { runState } = get();

    const result: ExecutionWorkflowMismatch = {
      missingInWorkflow: [],
      missingInExecution: [],
      hasMismatch: false,
    };

    if (!runState) {
      return result;
    }

    // Get all node IDs from workflow
    const workflowNodeIds = new Set<string>();
    for (const triggerName of Object.keys(workflow.definition.triggers || {})) {
      workflowNodeIds.add(`trigger-${triggerName}`);
    }
    for (const actionName of Object.keys(workflow.definition.actions || {})) {
      workflowNodeIds.add(`action-${actionName}`);
    }

    // Get all node IDs from execution
    const executionNodeIds = new Set(Object.keys(runState.nodeRunData));

    // Find nodes in execution but not in workflow
    for (const nodeId of executionNodeIds) {
      if (!workflowNodeIds.has(nodeId)) {
        result.missingInWorkflow.push(nodeId);
      }
    }

    // Find nodes in workflow but not in execution
    for (const nodeId of workflowNodeIds) {
      if (!executionNodeIds.has(nodeId)) {
        result.missingInExecution.push(nodeId);
      }
    }

    result.hasMismatch =
      result.missingInWorkflow.length > 0 || result.missingInExecution.length > 0;

    return result;
  },

  markSessionWarningShown: () => {
    set({ hasShownSessionWarning: true });
  },

  detectDirtyNodes: (currentWorkflow: WorkflowDefinition): DirtyNodesResult => {
    const { runState } = get();

    const result: DirtyNodesResult = {
      dirtyNodes: [],
      dirtyNodeIds: new Set<string>(),
      hasDirtyNodes: false,
    };

    if (!runState?.originalWorkflow) {
      return result;
    }

    const originalWorkflow = runState.originalWorkflow as WorkflowDefinition;

    // Helper to compare objects (ignoring metadata like position)
    const compareNodeConfig = (
      original: Action | Trigger | undefined,
      current: Action | Trigger | undefined
    ): boolean => {
      if (!original && !current) return true;
      if (!original || !current) return false;

      // Compare type
      if (original.type !== current.type) return false;

      // Compare inputs (the main configuration)
      const originalInputs = JSON.stringify(original.inputs || {});
      const currentInputs = JSON.stringify(current.inputs || {});
      if (originalInputs !== currentInputs) return false;

      // Compare runAfter (connections) for actions
      if ('runAfter' in original || 'runAfter' in current) {
        const originalRunAfter = JSON.stringify((original as Action).runAfter || {});
        const currentRunAfter = JSON.stringify((current as Action).runAfter || {});
        if (originalRunAfter !== currentRunAfter) return false;
      }

      return true;
    };

    // Build dependency graph for cascade detection
    const getDependents = (nodeId: string): string[] => {
      const dependents: string[] = [];
      const nodeName = nodeId.replace(/^(trigger-|action-)/, '');

      for (const [actionName, action] of Object.entries(currentWorkflow.definition.actions || {})) {
        const runAfter = action.runAfter || {};
        if (nodeName in runAfter) {
          dependents.push(`action-${actionName}`);
        }
      }

      return dependents;
    };

    // Check triggers
    const originalTriggers = originalWorkflow.definition?.triggers || {};
    const currentTriggers = currentWorkflow.definition?.triggers || {};

    for (const triggerName of Object.keys(currentTriggers)) {
      const nodeId = `trigger-${triggerName}`;
      const original = originalTriggers[triggerName];
      const current = currentTriggers[triggerName];

      if (!original) {
        result.dirtyNodes.push({
          nodeId,
          reason: 'node_added',
          description: 'This trigger was added after the execution',
        });
        result.dirtyNodeIds.add(nodeId);
      } else if (!compareNodeConfig(original, current)) {
        result.dirtyNodes.push({
          nodeId,
          reason: 'parameters_changed',
          description: 'Trigger configuration has changed since the execution',
        });
        result.dirtyNodeIds.add(nodeId);
      }
    }

    // Check for removed triggers
    for (const triggerName of Object.keys(originalTriggers)) {
      if (!currentTriggers[triggerName]) {
        const nodeId = `trigger-${triggerName}`;
        result.dirtyNodes.push({
          nodeId,
          reason: 'node_removed',
          description: 'This trigger was removed after the execution',
        });
        result.dirtyNodeIds.add(nodeId);
      }
    }

    // Check actions
    const originalActions = originalWorkflow.definition?.actions || {};
    const currentActions = currentWorkflow.definition?.actions || {};

    for (const actionName of Object.keys(currentActions)) {
      const nodeId = `action-${actionName}`;
      const original = originalActions[actionName];
      const current = currentActions[actionName];

      if (!original) {
        result.dirtyNodes.push({
          nodeId,
          reason: 'node_added',
          description: 'This action was added after the execution',
        });
        result.dirtyNodeIds.add(nodeId);
      } else if (!compareNodeConfig(original, current)) {
        // Check if it's specifically a connection change
        const originalRunAfter = JSON.stringify(original.runAfter || {});
        const currentRunAfter = JSON.stringify(current.runAfter || {});

        if (originalRunAfter !== currentRunAfter) {
          result.dirtyNodes.push({
            nodeId,
            reason: 'connections_changed',
            description: 'Action connections have changed since the execution',
          });
        } else {
          result.dirtyNodes.push({
            nodeId,
            reason: 'parameters_changed',
            description: 'Action configuration has changed since the execution',
          });
        }
        result.dirtyNodeIds.add(nodeId);
      }
    }

    // Check for removed actions
    for (const actionName of Object.keys(originalActions)) {
      if (!currentActions[actionName]) {
        const nodeId = `action-${actionName}`;
        result.dirtyNodes.push({
          nodeId,
          reason: 'node_removed',
          description: 'This action was removed after the execution',
        });
        result.dirtyNodeIds.add(nodeId);
      }
    }

    // Cascade: mark dependents of dirty nodes as dirty too
    const cascadeDirty = (dirtyNodeIds: Set<string>) => {
      let changed = true;
      while (changed) {
        changed = false;
        for (const nodeId of dirtyNodeIds) {
          const dependents = getDependents(nodeId);
          for (const dependent of dependents) {
            if (!dirtyNodeIds.has(dependent)) {
              dirtyNodeIds.add(dependent);
              result.dirtyNodes.push({
                nodeId: dependent,
                reason: 'upstream_dirty',
                description: 'An upstream node has changed, so this node may produce different results',
              });
              changed = true;
            }
          }
        }
      }
    };

    cascadeDirty(result.dirtyNodeIds);

    result.hasDirtyNodes = result.dirtyNodes.length > 0;
    return result;
  },

  isNodeDirty: (nodeId: string, currentWorkflow: WorkflowDefinition): boolean => {
    const { detectDirtyNodes } = get();
    const result = detectDirtyNodes(currentWorkflow);
    return result.dirtyNodeIds.has(nodeId);
  },

  getNodeExecutions: () => {
    const { runState } = get();
    if (!runState) return [];

    // Convert NodeRunData to NodeExecution format
    // The formats are nearly identical, just need to add missing fields
    return Object.values(runState.nodeRunData).map((nodeData) => ({
      nodeId: nodeData.nodeId,
      nodeName: nodeData.nodeName,
      nodeType: nodeData.nodeType,
      status: nodeData.status,
      startTime: nodeData.startTime,
      endTime: nodeData.endTime,
      duration: nodeData.duration,
      input: nodeData.input,
      output: nodeData.output,
      error: nodeData.error,
      iterations: nodeData.iterations?.map((iter) => ({
        index: iter.index,
        status: iter.status,
        startTime: iter.startTime,
        endTime: iter.endTime,
        duration: iter.duration,
        input: iter.input,
        output: iter.output,
        toolExecutions: iter.toolExecutions?.map((tool) => ({
          toolId: tool.toolId,
          toolName: tool.toolName,
          actionName: tool.actionName,
          status: tool.status,
          startTime: tool.startTime,
          endTime: tool.endTime,
          duration: tool.duration,
          input: tool.input,
          output: tool.output,
        })),
      })),
      // Use iterationCount or derive from iterations array length
      iterationCount: nodeData.iterationCount ?? (nodeData.iterations?.length || undefined),
    }));
  },
}));
